/**
 * Category Service
 * Handles API calls for category management
 */

import API_BASE_URL from '../config';

const API_BASE = API_BASE_URL;

export interface Category {
  id: number;
  key: string;
  label: string;
  parent_key: string | null;
  sort_order: number;
  children?: Category[];
}

export interface CategoryResponse {
  categories: Category[];
}

/**
 * Get all categories
 * @param token - Auth token
 * @param options - Optional filters
 * @returns Promise with categories array
 */
export async function getCategories(token: string, options?: {
  parent?: string;
  search?: string;
  flat?: boolean;
}): Promise<Category[]> {
  const params = new URLSearchParams();
  if (options?.parent) params.append('parent', options.parent);
  if (options?.search) params.append('search', options.search);
  if (options?.flat !== undefined) params.append('flat', String(options.flat));

  const response = await fetch(`${API_BASE}/api/categories?${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch categories: ${response.statusText}`);
  }

  const data: CategoryResponse = await response.json();
  return data.categories;
}

/**
 * Get only parent (top-level) categories
 * @param token - Auth token
 * @returns Promise with parent categories array
 */
export async function getParentCategories(token: string): Promise<Category[]> {
  const response = await fetch(`${API_BASE}/api/categories/parents`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch parent categories: ${response.statusText}`);
  }

  const data: CategoryResponse = await response.json();
  return data.categories;
}

/**
 * Search categories with autocomplete
 * @param token - Auth token
 * @param query - Search query
 * @param limit - Max results (default 10)
 * @returns Promise with matching categories
 */
export async function searchCategories(token: string, query: string, limit: number = 10): Promise<Category[]> {
  const params = new URLSearchParams({
    q: query,
    limit: String(limit),
  });

  const response = await fetch(`${API_BASE}/api/categories/search?${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error(`Failed to search categories: ${response.statusText}`);
  }

  const data: CategoryResponse = await response.json();
  return data.categories;
}

/**
 * Get item categories (from item_categories junction table)
 * @param token - Auth token
 * @param itemId - Item ID
 * @returns Promise with categories assigned to the item
 */
export async function getItemCategories(token: string, itemId: number): Promise<Category[]> {
  const response = await fetch(`${API_BASE}/api/items/${itemId}/categories`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch item categories: ${response.statusText}`);
  }

  const data: CategoryResponse = await response.json();
  return data.categories;
}

/**
 * Update item categories
 * @param token - Auth token
 * @param itemId - Item ID
 * @param categoryIds - Array of category IDs to assign
 * @returns Promise
 */
export async function updateItemCategories(token: string, itemId: number, categoryIds: number[]): Promise<void> {
  const response = await fetch(`${API_BASE}/api/items/${itemId}/categories`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ category_ids: categoryIds }),
  });

  if (!response.ok) {
    throw new Error(`Failed to update item categories: ${response.statusText}`);
  }
}
